import React from "react";
import { dateSimplewithComma } from "../../utils/constants";
import { Tooltip } from "@mui/material";
import loader from "../../assets/button-loader.svg";
import ReusableMenu from "../menu/ReusableMenu";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const CurrentMonthTransfersTable = ({
  transfers = [],
  prevPage,
  nextPage,
  paginate,
  currentPage,
  indexOfFirstItem,
  indexOfLastItem,
  itemsPerPage,
  isFetching,
  handleItemsPerPageChange,
  totalCount
}) => {
  // Format wallet address: first 8 chars + ... + last 6 chars
  const formatWalletAddress = (address) => {
    if (!address) return '';
    if (address.length <= 16) return address;
    return `${address.substring(0, 8)}...${address.substring(address.length - 6)}`;
  };

  const handleExportPDF = () => {
    const doc = new jsPDF({ orientation: "landscape" });
    const tableColumn = [
      "Wallet ID",
      "Email Address",
      "Plan Name",
      "Status",
      "Amount",
      "Payment Date"
    ];
    const tableRows = transfers.map((item) => [
      item.id,
      item.email_address,
      item.subscription?.tier_name || 'N/A',
      item.subscription?.status || 'N/A',
      item.subscription?.tier_amount ? `$${item.subscription?.tier_amount}` : 'N/A',
      dateSimplewithComma(item.subscription?.payment?.date)
    ]);

    doc.setFontSize(18);
    doc.text("Current Month Transfers List", 14, 18);

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 24,
      styles: {
        fontSize: 10,
        cellPadding: 3,
        valign: 'middle',
      },
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: 255,
        fontStyle: 'bold',
        halign: 'center',
      },
      columnStyles: {
        0: { cellWidth: 60, halign: 'left' },   
        1: { cellWidth: 60, halign: 'left' },   
        2: { cellWidth: 40, halign: 'left' },   
        3: { cellWidth: 32, halign: 'center' }, 
        4: { cellWidth: 32, halign: 'center' }, 
        5: { cellWidth: 38, halign: 'center' }, 
      },
      bodyStyles: {
        halign: 'left',
      },
      didParseCell: function (data) {
        if ([3, 4, 5].includes(data.column.index)) {
          data.cell.styles.halign = 'center';
        }
      },
      alternateRowStyles: { fillColor: [245, 245, 245] },
    });
    doc.save("current-month-transfers-list.pdf");
  };

  return (
    <>
      <div className="flex justify-end mb-4">
        <button
          className="bg-[#353535] hover:bg-[#232323] text-[#D9D9D9] font-semibold py-2 px-6 rounded-lg border border-[#313D4F] shadow transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={handleExportPDF}
          disabled={isFetching || transfers.length === 0}
        >
          Export PDF
        </button>
      </div>
      <div className="rounded-xl bg-[#1E1E1E] shadow-lg border border-[#313D4F]">
        <div className="inline-block w-full rounded-lg overflow-auto">
          <div className="h-[60vh] overflow-clip rounded-xl flex flex-col">

            <table className="w-full table-fixed border-separate border-spacing-0">
              <thead className="text-[#D9D9D9] bg-[#353535] sticky top-0 z-10">
                <tr className="border-b border-[#313D4F]">
                  <th className="w-[16.6%] px-4 py-4 text-left text-sm font-semibold tracking-wider ">Wallet ID</th>
                  <th className="w-[16.6%] px-[35px] py-4 text-left text-sm font-semibold tracking-wider">Email Address</th>
                  <th className="w-[16.6%] px-10 py-4 text-left text-sm font-semibold tracking-wider">Plan Name</th>
                  <th className="w-[16.6%] px-6 py-4 text-center text-sm font-semibold tracking-wider">Status</th>
                  <th className="w-[16.6%] px-4 py-4 text-center text-sm font-semibold tracking-wider">Amount</th>
                  <th className="w-[16.6%] px-4 py-4 text-center text-sm font-semibold tracking-wider">Payment Date</th>
                </tr>
              </thead>
            </table>
            <div className="flex-1 overflow-y-auto">
              <table className="w-full table-fixed">
                <tbody className="text-[#F3F3F3] bg-[#1E1E1E] divide-y divide-[#313D4F]">
                  {transfers?.map((transfer, index) => (
                    <tr key={`${transfer.id}-${index}`} className="hover:bg-[#2A2A2A] transition-colors">
                      <td className="w-[20%] px-4 py-4">
                        <Tooltip
                          placement="top-start"
                          className="cursor-pointer"
                          componentsProps={{
                            tooltip: {
                              sx: {
                                border: "1px solid #313D4F",
                                fontSize: "12px",
                                bgcolor: "#1E1E1E",
                                color: "#F3F3F3",
                                borderRadius: "8px",
                                padding: "8px",
                                maxWidth: "400px"
                              }
                            }
                          }}
                          title={transfer.id}
                        >
                          <span className="truncate block max-w-[200px] text-sm font-mono cursor-pointer select-all" style={{lineHeight: '1.5'}}>
                            {formatWalletAddress(transfer.id)}
                          </span>
                        </Tooltip>
                      </td>
                      <td className="w-[20%] px-4 py-4">
                        <Tooltip
                          placement="top-start"
                          className="cursor-pointer"
                          componentsProps={{
                            tooltip: {
                              sx: {
                                border: "1px solid #313D4F",
                                fontSize: "12px",
                                bgcolor: "#1E1E1E",
                                color: "#F3F3F3",
                                borderRadius: "8px",
                                padding: "8px",
                                maxWidth: "300px"
                              }
                            }
                          }}
                          title={transfer.email_address}
                        >
                          <span className="font-semibold text-white truncate block max-w-[180px]">{transfer.email_address}</span>
                        </Tooltip>
                      </td>
                      <td className="w-[16.6%] px-4 py-4">
                        {transfer.subscription?.tier_name || 'N/A'}
                      </td>
                      <td className="w-[16.6%] px-4 py-4 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs mr-10 ${
                          transfer.subscription?.status === 'ACTIVE' ? 'bg-green-500/10 text-green-500' : 
                          transfer.subscription?.status === 'INACTIVE' ? 'bg-red-500/10 text-red-500' : 
                          'bg-gray-500/10 text-gray-500'
                        }`}>
                          {transfer.subscription?.status || 'N/A'}
                        </span>
                      </td>
                      <td className="w-[16.6%] px-4 py-4 text-center">
                        {transfer.subscription?.tier_amount ? `$${transfer.subscription?.tier_amount}` : 'N/A'}
                      </td>
                      <td className="w-[16.6%] px-4 py-4 text-center">
                        {dateSimplewithComma(transfer.subscription?.payment?.date)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {isFetching && (
              <div className="flex min-h-[50vh] justify-center items-center">
                <img src={loader} alt="loading" className="w-10 h-10" />
              </div>
            )}
          </div>
        </div>
      </div>
      {transfers.length > 0 && (
        <div className="flex justify-between items-center mt-2">
          <div className="opacity-60 text-zinc-100 text-sm font-semibold">
            Showing {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, totalCount)} of {totalCount} entries
          </div>
          <div className="flex mt-4 justify-center items-center">
            <div className="justify-start items-center gap-2.5 inline-flex pr-3">
              <div className="text-zinc-100 text-sm font-semibold">
                Items per page
              </div>
              <div className="w-[70px] flex-col justify-start items-start gap-2 inline-flex">
                <div className="relative inline-block text-left">
                  <ReusableMenu
                    buttonIcon={require('../../assets/down-arrow.svg').default}
                    isFetching={isFetching}
                    menuItems={[5, 10, 25, 50]}
                    buttonText={itemsPerPage}
                    handleChangeFun={handleItemsPerPageChange}
                  />
                </div>
              </div>
            </div>
            <div className="w-[157px] h-[42px] flex-col justify-start items-start gap-[30px] inline-flex">
              <div className="justify-start items-center gap-2 inline-flex">
                <div className="text-zinc-100 text-sm font-semibold">Page</div>
                <div className="w-[70px] flex-col justify-start items-start gap-2 inline-flex">
                  <div className="relative inline-block text-left">
                    <ReusableMenu
                      buttonIcon={require('../../assets/down-arrow.svg').default}
                      menuItems={Array.from({ length: Math.ceil(totalCount / itemsPerPage) }, (_, i) => i + 1)}
                      buttonText={currentPage}
                      handleChangeFun={paginate}
                      isFetching={isFetching}
                    />
                  </div>
                </div>
                <div className="text-zinc-100 text-sm font-semibold">
                  of {Math.ceil(totalCount / itemsPerPage)}
                </div>
              </div>
            </div>
            <div className="w-[86px] h-[30px] ml-2 relative">
              <div className="w-[86px] h-[30px] p-2 justify-center items-center flex bg-[#1E1E1E] rounded-[27px] border border-[#787878]" />
              <button disabled={isFetching} onClick={prevPage}>
                <img
                  src={require('../../assets/rightchev.svg').default}
                  alt="right"
                  className="w-auto h-[15px] rotate-180 left-[10px] top-[7px] absolute cursor-pointer"
                />
              </button>
              <button disabled={isFetching} onClick={nextPage}>
                <img
                  src={require('../../assets/rightchev.svg').default}
                  alt="left"
                  className="rotate-270 opacity-90 w-auto h-[15px] left-[55px] top-[7px] absolute cursor-pointer"
                />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CurrentMonthTransfersTable;
